import React, { useEffect, useRef, useState } from 'react';
import { clearLocalStorage, Col, getItemWithExpiration, Row } from '../../lib/utils';
import { colors } from '@/lib/colors';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import NewProfileImage from '../NewProfileImage';
import SvgIcon from '../SvgIcon';
import BellIcon from '../AlertCenter/BellIcon';
import { useAdminAlertsUnreadCountQuery } from '../../services/useAdminAlertsService';

const GnbMyInfo = ({
    authObject,
    setIsVisibleInfo,
    navigate,
    onClickTerm,
    profileButtonRef,
    onClickPermissionModal,
    onClickTabLockModal,
    onClickMemberNotificationModal,
    onClickAlertCenter,
    onClickUsageGuide,
}: any) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const [auth] = useState(getItemWithExpiration('authObject'));
    const centerName = auth?.centerName || '센터 이름 없음';
    const adminName = auth?.name || '관리자';
    const accessToken = getItemWithExpiration('authorization') ?? '';
    const { data: unreadData } = useAdminAlertsUnreadCountQuery({ accessToken });
    const unreadCount = unreadData?.count ?? 0;

    const handleClickOutside = (event: MouseEvent) => {
        if (
            modalRef.current &&
            !modalRef.current.contains(event.target as Node) &&
            profileButtonRef.current &&
            !profileButtonRef.current.contains(event.target as Node)
        ) {
            setIsVisibleInfo(false);
        }
    };

    const handleMyPageClick = () => {
        navigate('/myPage');
        setIsVisibleInfo(false);
    };

    type MenuItem = {
        icon?: keyof typeof import('../../types/svg');
        iconNode?: React.ReactNode;
        label?: string;
        onClick?: (e?: any) => void;
        isDestructive?: boolean;
        divider?: boolean;
        trailing?: React.ReactNode;
    };

    const unreadBadge =
        unreadCount > 0 ? (
            <span
                className="min-w-[20px] h-[18px] px-[6px] rounded-full flex items-center justify-center"
                style={{
                    backgroundColor: '#DC2626',
                    color: '#FFFFFF',
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: '14px',
                }}
            >
                {unreadCount > 99 ? '99+' : unreadCount}
            </span>
        ) : null;

    const menuItems: MenuItem[] = [
        {
            icon: 'SvgSetting',
            label: '권한 설정',
            onClick: () => {
                onClickPermissionModal();
                setIsVisibleInfo(false);
            },
        },
        {
            icon: 'SvgUser',
            label: '마이페이지',
            onClick: handleMyPageClick,
        },
        {
            icon: 'SvgLock',
            label: '탭 잠금 설정',
            onClick: () => {
                onClickTabLockModal && onClickTabLockModal();
                setIsVisibleInfo(false);
            },
        },
        {
            // 벨 아이콘은 바로 아래 "알림센터"가 쓴다 — 혼동을 피해 수강권 아이콘 (설계 7-2)
            icon: 'SvgMembershipTicket',
            label: '회원 알림 설정',
            onClick: () => {
                onClickMemberNotificationModal?.();
                setIsVisibleInfo(false);
            },
        },
        { divider: true },
        {
            iconNode: <BellIcon size={16} color={colors.LAVEL_4} />,
            label: '알림센터',
            trailing: unreadBadge,
            onClick: () => {
                onClickAlertCenter?.();
            },
        },
        {
            icon: 'SvgQuestion',
            label: '이용방법',
            onClick: () => {
                onClickUsageGuide?.();
            },
        },
        {
            icon: 'SvgChat',
            label: '실시간 1:1 문의하기',
            onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                window.open('http://pf.kakao.com/_ICxhcb/chat', '_blank');
                setIsVisibleInfo(false);
            },
        },
        {
            icon: 'SvgDocument',
            label: '이용약관 / 개인정보 정책',
            onClick: () => {
                onClickTerm();
                setIsVisibleInfo(false);
            },
        },
        { divider: true },
        {
            icon: 'SvgLogout',
            label: '로그아웃',
            onClick: () => {
                clearLocalStorage();
                navigate('/signIn');
                setIsVisibleInfo(false);
            },
            isDestructive: true,
        },
    ];

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div
            ref={modalRef}
            className="absolute top-full right-0 mt-[12px] w-[292px] flex flex-col overflow-hidden rounded-[20px] border border-[#E2E8F0]"
            style={{
                background: 'rgba(255,255,255,0.96)',
                boxShadow: '0 24px 48px rgba(15,23,42,0.14)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
            }}
        >
            {/* Profile header */}
            <div
                className="px-[20px] pt-[20px] pb-[18px] cursor-pointer transition-colors"
                style={{
                    background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)',
                }}
                onClick={handleMyPageClick}
            >
                <Row className="flex w-full items-center">
                    <NewProfileImage
                        imageUrl={auth?.profileImageUrl || ''}
                        size={44}
                        iconSize={22}
                        circleColor="#F2F4F6"
                        className="flex mr-[12px]"
                    />
                    <Col className="flex-1 overflow-hidden">
                        <MediumText
                            fontSize={16}
                            lineHeight={22}
                            color={colors.Text_1}
                            className="overflow-hidden whitespace-nowrap text-ellipsis"
                        >
                            {adminName}
                        </MediumText>
                        <DemiLightText
                            fontSize={13}
                            lineHeight={18}
                            color={colors.LAVEL_4}
                            className="overflow-hidden whitespace-nowrap text-ellipsis"
                        >
                            {centerName}
                        </DemiLightText>
                    </Col>
                    <div
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#EFF6FF',
                            border: '1px solid #BFDBFE',
                        }}
                    >
                        <SvgIcon name="SvgRightStrokeArrow" size={14} fill={colors.trans} stroke="#2563EB" />
                    </div>
                </Row>
            </div>

            {/* Divider */}
            <div className="h-[1px] bg-[#EEF2F7] mx-[16px]" />

            {/* Menu items */}
            <div className="flex flex-col py-[10px] px-[10px]">
                {menuItems.map((item, index) =>
                    item.divider ? (
                        <div key={`divider-${index}`} className="h-[1px] bg-[#EEF2F7] my-[6px] mx-[10px]" />
                    ) : (
                        <button
                            key={item.label}
                            onClick={item.onClick}
                            className="flex w-full items-center text-left cursor-pointer px-[12px] py-[11px] rounded-[14px] transition-colors duration-150 gap-[10px]"
                            style={{
                                backgroundColor: item.isDestructive ? '#FFF7F7' : 'transparent',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = item.isDestructive ? '#FEECEC' : '#F8FAFC';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = item.isDestructive ? '#FFF7F7' : 'transparent';
                            }}
                        >
                            <div
                                className="w-[28px] h-[28px] flex items-center justify-center flex-shrink-0 rounded-[10px]"
                                style={{
                                    backgroundColor: item.isDestructive ? '#FEE2E2' : '#F1F5F9',
                                }}
                            >
                                {item.iconNode
                                    ? item.iconNode
                                    : item.icon && (
                                          <SvgIcon
                                              name={item.icon}
                                              size={16}
                                              fill={item.isDestructive ? '#E53935' : colors.LAVEL_4}
                                              stroke={item.icon === 'SvgLock' ? colors.LAVEL_4 : undefined}
                                          />
                                      )}
                            </div>
                            <RegularText
                                fontSize={14}
                                lineHeight={20}
                                color={item.isDestructive ? '#E53935' : colors.Text_1}
                            >
                                {item.label}
                            </RegularText>
                            {item.trailing && <div className="ml-auto">{item.trailing}</div>}
                        </button>
                    ),
                )}
            </div>
        </div>
    );
};

export default GnbMyInfo;
